<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import AutomationSelect from 'dashboard/modules/kanban/components/automations/AutomationSelect.vue';
import TaskGroupItemRecurrencePopover from 'dashboard/modules/kanban/components/task_groups/TaskGroupItemRecurrencePopover.vue';

const props = defineProps({
  modelValue: { type: Object, required: true },
  allowCardLeader: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

// ── local copy ───────────────────────────────────────────────────
const local = ref({ ...props.modelValue });
watch(
  () => props.modelValue,
  v => {
    local.value = { ...v };
  },
  { deep: true }
);

function patch(field, value) {
  local.value = { ...local.value, [field]: value };
  emit('update:modelValue', { ...local.value });
}

// ── recorrência ──────────────────────────────────────────────────
const showRecurrencePopover = ref(false);
const recurrenceBtnRef = ref(null);

function onRecurrenceUpdate(fields) {
  local.value = { ...local.value, ...fields };
  emit('update:modelValue', { ...local.value });
}

const RECURRENCE_LABELS = {
  daily: 'TASKS.RECURRENCE.DAILY',
  weekdays: 'TASKS.RECURRENCE.WEEKDAYS',
  weekly: 'TASKS.RECURRENCE.WEEKLY',
  monthly: 'TASKS.RECURRENCE.MONTHLY',
  custom: 'TASKS.RECURRENCE.CUSTOM',
  flexible: 'TASKS.RECURRENCE.FLEXIBLE',
};

const recurrenceLabel = computed(() => {
  const type = local.value.recurrence_type;
  if (!type) return t('TASKS.RECURRENCE.LABEL');
  if (type === 'custom' && local.value.recurrence_interval)
    return `${local.value.recurrence_interval}x`;
  return RECURRENCE_LABELS[type] ? t(RECURRENCE_LABELS[type]) : type;
});

// ── prazo ─────────────────────────────────────────────────────────
const DUE_UNITS = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Dias' },
  { value: 'months', label: 'Meses' },
  { value: 'years', label: 'Anos' },
];

// ── prioridade ────────────────────────────────────────────────────
const showPriorityMenu = ref(false);
const prioritySearch = ref('');
const priorityHighlight = ref(0);

const PRIORITY_OPTIONS = [
  {
    key: 'urgent',
    label: 'Urgente',
    dot: 'text-n-ruby-9',
    flag: 'text-n-ruby-9',
  },
  {
    key: 'high',
    label: 'Prioridade',
    dot: 'text-n-amber-9',
    flag: 'text-n-amber-9',
  },
  {
    key: 'normal',
    label: 'Normal',
    dot: 'text-n-blue-9',
    flag: 'text-n-blue-9',
  },
  {
    key: 'no_priority',
    label: 'Baixa',
    dot: 'text-n-slate-6',
    flag: 'text-n-slate-8',
  },
];

const filteredPriorityOptions = computed(() => {
  const q = prioritySearch.value.toLowerCase();
  if (!q) return PRIORITY_OPTIONS;
  return PRIORITY_OPTIONS.filter(o => o.label.toLowerCase().includes(q));
});

const currentPriority = computed(
  () =>
    PRIORITY_OPTIONS.find(o => o.key === local.value.priority) ??
    PRIORITY_OPTIONS[3]
);

function setPriority(key) {
  showPriorityMenu.value = false;
  prioritySearch.value = '';
  patch('priority', key);
}

function onPriorityKeydown(e) {
  if (!showPriorityMenu.value) return;
  if (e.key === 'Escape') {
    showPriorityMenu.value = false;
    prioritySearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    priorityHighlight.value = Math.min(
      priorityHighlight.value + 1,
      filteredPriorityOptions.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    priorityHighlight.value = Math.max(priorityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const o = filteredPriorityOptions.value[priorityHighlight.value];
    if (o) setPriority(o.key);
  } else if (e.key === 'Backspace') {
    prioritySearch.value = prioritySearch.value.slice(0, -1);
    priorityHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    prioritySearch.value += e.key;
    priorityHighlight.value = 0;
  }
}

watch(showPriorityMenu, val => {
  priorityHighlight.value = 0;
  if (val) window.addEventListener('keydown', onPriorityKeydown);
  else {
    prioritySearch.value = '';
    window.removeEventListener('keydown', onPriorityKeydown);
  }
});

// ── responsável ───────────────────────────────────────────────────
const showAssigneeMenu = ref(false);
const assigneeSearch = ref('');
const assigneeHighlight = ref(0);

const filteredAssigneeAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const assigneeNavigable = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  const items = filteredAssigneeAgents.value.map(a => ({ id: a.id }));
  if (q) {
    const showLeader = props.allowCardLeader && 'líder do card'.includes(q);
    const showUnassign = 'remover'.includes(q);
    return [
      ...(showLeader ? [{ id: 'card_leader' }] : []),
      ...items,
      ...(showUnassign ? [{ id: null }] : []),
    ];
  }
  return [
    ...(props.allowCardLeader ? [{ id: 'card_leader' }] : []),
    ...items,
    { id: null },
  ];
});

const selectedAssignee = computed(
  () => (agents.value ?? []).find(a => a.id === local.value.assignee_id) ?? null
);

function setAssignee(agentOrKey) {
  showAssigneeMenu.value = false;
  assigneeSearch.value = '';
  if (agentOrKey === 'card_leader') patch('assignee_id', 'card_leader');
  else if (agentOrKey === null || agentOrKey === undefined)
    patch('assignee_id', null);
  else patch('assignee_id', agentOrKey.id);
}

function isAssigneeHighlighted(id) {
  return assigneeNavigable.value[assigneeHighlight.value]?.id === id;
}

function onAssigneeKeydown(e) {
  if (!showAssigneeMenu.value) return;
  if (e.key === 'Escape') {
    showAssigneeMenu.value = false;
    assigneeSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    assigneeHighlight.value = Math.min(
      assigneeHighlight.value + 1,
      assigneeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    assigneeHighlight.value = Math.max(assigneeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const nav = assigneeNavigable.value[assigneeHighlight.value];
    if (nav) {
      if (nav.id === 'card_leader') setAssignee('card_leader');
      else if (nav.id === null) setAssignee(null);
      else setAssignee(agents.value?.find(a => a.id === nav.id));
    }
  } else if (e.key === 'Backspace') {
    assigneeSearch.value = assigneeSearch.value.slice(0, -1);
    assigneeHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    assigneeSearch.value += e.key;
    assigneeHighlight.value = 0;
  }
}

watch(showAssigneeMenu, val => {
  assigneeHighlight.value = 0;
  if (val) window.addEventListener('keydown', onAssigneeKeydown);
  else {
    assigneeSearch.value = '';
    window.removeEventListener('keydown', onAssigneeKeydown);
  }
});

// ── tipo ──────────────────────────────────────────────────────────
const showTypeMenu = ref(false);
const typeSearch = ref('');
const typeHighlight = ref(0);

const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

const filteredTaskTypes = computed(() => {
  const q = typeSearch.value.toLowerCase();
  if (!q) return TASK_TYPES;
  return TASK_TYPES.filter(tp => tp.label.toLowerCase().includes(q));
});

const currentType = computed(
  () => TASK_TYPES.find(tp => tp.key === local.value.task_type) ?? TASK_TYPES[0]
);

function setTaskType(key) {
  showTypeMenu.value = false;
  typeSearch.value = '';
  patch('task_type', key);
}

function onTypeKeydown(e) {
  if (!showTypeMenu.value) return;
  if (e.key === 'Escape') {
    showTypeMenu.value = false;
    typeSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    typeHighlight.value = Math.min(
      typeHighlight.value + 1,
      filteredTaskTypes.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    typeHighlight.value = Math.max(typeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const tp = filteredTaskTypes.value[typeHighlight.value];
    if (tp) setTaskType(tp.key);
  } else if (e.key === 'Backspace') {
    typeSearch.value = typeSearch.value.slice(0, -1);
    typeHighlight.value = 0;
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    typeSearch.value += e.key;
    typeHighlight.value = 0;
  }
}

watch(showTypeMenu, val => {
  typeHighlight.value = 0;
  if (val) window.addEventListener('keydown', onTypeKeydown);
  else {
    typeSearch.value = '';
    window.removeEventListener('keydown', onTypeKeydown);
  }
});

// ── etiquetas ─────────────────────────────────────────────────────
const showLabelMenu = ref(false);
const labelSearch = ref('');
const labelHighlight = ref(0);

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  if (!q) return accountLabels.value ?? [];
  return (accountLabels.value ?? [])
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

const labelNavigable = computed(() =>
  filteredLabels.value.map(l => ({ id: l.title }))
);

watch(labelNavigable, () => {
  labelHighlight.value = 0;
});
watch(labelSearch, () => {
  labelHighlight.value = 0;
});

function isLabelActive(title) {
  return (local.value.label_list ?? []).includes(title);
}

function tagColor(label) {
  const found = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (found?.color) return { bg: `${found.color}22`, text: found.color };
  return { bg: 'rgba(99,102,241,0.12)', text: '#4338ca' };
}

function toggleLabel(title) {
  const existing = local.value.label_list ?? [];
  const updated = existing.includes(title)
    ? existing.filter(l => l !== title)
    : [...existing, title];
  patch('label_list', updated);
}

function removeLabel(tag) {
  patch(
    'label_list',
    (local.value.label_list ?? []).filter(l => l !== tag)
  );
}

function onLabelKeydown(e) {
  if (!showLabelMenu.value) return;
  if (e.key === 'Escape') {
    showLabelMenu.value = false;
    labelSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    labelHighlight.value = Math.min(
      labelHighlight.value + 1,
      labelNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    labelHighlight.value = Math.max(labelHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = labelNavigable.value[labelHighlight.value];
    if (item) toggleLabel(item.id);
  } else if (e.key === 'Backspace') {
    labelSearch.value = labelSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    labelSearch.value += e.key;
  }
}

watch(showLabelMenu, val => {
  labelHighlight.value = 0;
  if (val) window.addEventListener('keydown', onLabelKeydown);
  else {
    labelSearch.value = '';
    window.removeEventListener('keydown', onLabelKeydown);
  }
});

// ── avatar ────────────────────────────────────────────────────────
const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

// ── fechar dropdowns ──────────────────────────────────────────────
const showChecklistAssignee = ref(null);

function closeDropdowns() {
  showPriorityMenu.value = false;
  showAssigneeMenu.value = false;
  showTypeMenu.value = false;
  showLabelMenu.value = false;
  showChecklistAssignee.value = null;
}

defineExpose({ closeDropdowns });

// ── checklist ─────────────────────────────────────────────────────
const newChecklistText = ref('');
const newChecklistInputRef = ref(null);
const dragSrcIndex = ref(null);
const dragOverIndex = ref(null);

const checklistItems = computed(() => local.value.checklist_items ?? []);

const checklistProgress = computed(() => {
  const items = checklistItems.value;
  if (!items.length) return null;
  const done = items.filter(i => i.completed).length;
  return { done, total: items.length, label: `${done}/${items.length}` };
});

function saveChecklist(items) {
  patch('checklist_items', items);
}

function addChecklistItem() {
  const text = newChecklistText.value.trim();
  if (!text) return;
  saveChecklist([
    ...checklistItems.value,
    { id: crypto.randomUUID(), text, completed: false, assignee_id: null },
  ]);
  newChecklistText.value = '';
  nextTick(() => newChecklistInputRef.value?.focus());
}

function removeChecklistItem(itemId) {
  saveChecklist(checklistItems.value.filter(i => i.id !== itemId));
}

function setChecklistItemAssignee(itemId, agentId) {
  saveChecklist(
    checklistItems.value.map(i =>
      i.id === itemId ? { ...i, assignee_id: agentId } : i
    )
  );
  showChecklistAssignee.value = null;
}

function onDragStart(index) {
  dragSrcIndex.value = index;
}
function onDragOver(e, index) {
  e.preventDefault();
  dragOverIndex.value = index;
}
function onDragLeave() {
  dragOverIndex.value = null;
}
function onDragEnd() {
  dragSrcIndex.value = null;
  dragOverIndex.value = null;
}
function onDrop(index) {
  if (dragSrcIndex.value === null || dragSrcIndex.value === index) {
    dragSrcIndex.value = null;
    dragOverIndex.value = null;
    return;
  }
  const items = [...checklistItems.value];
  const [moved] = items.splice(dragSrcIndex.value, 1);
  items.splice(index, 0, moved);
  dragSrcIndex.value = null;
  dragOverIndex.value = null;
  saveChecklist(items);
}

function checklistItemAvatarStyle(assigneeId) {
  if (!assigneeId) return {};
  const agent = (agents.value ?? []).find(a => a.id === assigneeId);
  if (!agent) return {};
  if (agent.thumbnail) {
    return {
      backgroundImage: `url(${agent.thumbnail})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }
  return { backgroundColor: avatarColor(agent) };
}

onUnmounted(() => {
  window.removeEventListener('keydown', onPriorityKeydown);
  window.removeEventListener('keydown', onAssigneeKeydown);
  window.removeEventListener('keydown', onTypeKeydown);
  window.removeEventListener('keydown', onLabelKeydown);
});
</script>

<template>
  <div class="flex flex-col gap-4" @click="closeDropdowns">
    <!-- Grid de metadados -->
    <div class="border border-n-weak rounded-lg">
      <!-- Linha 1: Responsável | Prioridade -->
      <div
        class="grid grid-cols-2 divide-x divide-n-weak border-b border-n-weak"
      >
        <!-- Responsável -->
        <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.FIELDS.ASSIGNEE') }}
          </p>
          <button
            class="flex items-center gap-2 w-full text-left outline-none"
            @click="showAssigneeMenu = !showAssigneeMenu"
          >
            <div
              class="size-6 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white overflow-hidden"
              :class="
                local.assignee_id === 'card_leader'
                  ? 'bg-n-brand'
                  : !selectedAssignee
                    ? 'bg-n-surface-2 border border-n-weak'
                    : ''
              "
              :style="
                local.assignee_id !== 'card_leader' &&
                selectedAssignee?.thumbnail
                  ? {
                      backgroundImage: `url(${selectedAssignee.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : local.assignee_id !== 'card_leader' && selectedAssignee
                    ? { backgroundColor: avatarColor(selectedAssignee) }
                    : {}
              "
            >
              <span
                v-if="local.assignee_id === 'card_leader'"
                class="i-lucide-crown size-3 text-white"
              />
              <span v-else-if="selectedAssignee && !selectedAssignee.thumbnail">
                {{ initials(selectedAssignee.name) }}
              </span>
              <span
                v-else-if="!selectedAssignee"
                class="i-lucide-user-plus size-3 text-n-slate-9"
              />
            </div>
            <span
              v-if="showAssigneeMenu && assigneeSearch"
              class="text-sm text-n-slate-10 truncate flex-1 leading-none"
            >
              {{ assigneeSearch }}
            </span>
            <span
              v-else
              class="text-sm text-n-slate-12 truncate flex-1 leading-none"
            >
              {{
                local.assignee_id === 'card_leader'
                  ? t('KANBAN.TASK_GROUPS.LEADER.CARD_LEADER_ASSIGNEE')
                  : selectedAssignee?.name || '—'
              }}
            </span>
            <span
              class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
            />
          </button>
          <div
            v-if="showAssigneeMenu"
            class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[180px] py-1 overflow-hidden"
            @click.stop
          >
            <template v-if="allowCardLeader">
              <button
                class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
                :class="
                  isAssigneeHighlighted('card_leader')
                    ? 'bg-n-alpha-1 text-n-brand font-medium'
                    : local.assignee_id === 'card_leader'
                      ? 'text-n-brand font-medium hover:bg-n-alpha-1'
                      : 'text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="setAssignee('card_leader')"
                @mouseenter="
                  assigneeHighlight = assigneeNavigable.findIndex(
                    i => i.id === 'card_leader'
                  )
                "
              >
                <span
                  class="i-lucide-crown size-3.5 text-n-amber-9 flex-shrink-0"
                />
                {{ t('KANBAN.TASK_GROUPS.LEADER.CARD_LEADER_ASSIGNEE') }}
                <span
                  v-if="local.assignee_id === 'card_leader'"
                  class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
                />
              </button>
              <div class="border-t border-n-weak my-1" />
            </template>
            <button
              v-for="agent in filteredAssigneeAgents"
              :key="agent.id"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
              :class="
                isAssigneeHighlighted(agent.id)
                  ? 'bg-n-alpha-1 text-n-brand font-medium'
                  : agent.id === local.assignee_id
                    ? 'text-n-brand font-medium hover:bg-n-alpha-1'
                    : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="setAssignee(agent)"
              @mouseenter="
                assigneeHighlight = assigneeNavigable.findIndex(
                  i => i.id === agent.id
                )
              "
            >
              <div
                class="size-5 rounded-full flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white"
                :style="
                  agent.thumbnail
                    ? {
                        backgroundImage: `url(${agent.thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : { backgroundColor: avatarColor(agent) }
                "
              >
                <span v-if="!agent.thumbnail">{{ initials(agent.name) }}</span>
              </div>
              <span class="truncate">{{ agent.name }}</span>
              <span
                v-if="agent.id === local.assignee_id"
                class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
              />
            </button>
            <p
              v-if="assigneeSearch && !filteredAssigneeAgents.length"
              class="px-3 py-2 text-sm text-n-slate-10 italic"
            >
              {{ t('KANBAN.AUTOMATIONS.NOTHING_FOUND') }}
            </p>
            <template v-if="assigneeNavigable.some(i => i.id === null)">
              <div v-if="!assigneeSearch" class="border-t border-n-weak my-1" />
              <button
                class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
                :class="
                  isAssigneeHighlighted(null)
                    ? 'bg-n-alpha-1 text-n-brand font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="setAssignee(null)"
                @mouseenter="
                  assigneeHighlight = assigneeNavigable.findIndex(
                    i => i.id === null
                  )
                "
              >
                <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
                {{ t('TASKS.MODAL.UNASSIGN') }}
              </button>
            </template>
          </div>
        </div>

        <!-- Prioridade -->
        <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.FIELDS.PRIORITY') }}
          </p>
          <button
            class="flex items-center gap-1.5 text-sm w-full text-left outline-none"
            @click="showPriorityMenu = !showPriorityMenu"
          >
            <span
              class="i-lucide-flag size-3.5 flex-shrink-0"
              :class="currentPriority.flag"
            />
            <span
              v-if="showPriorityMenu && prioritySearch"
              class="text-n-slate-10 truncate flex-1 leading-none"
            >
              {{ prioritySearch }}
            </span>
            <span v-else class="text-n-slate-12 truncate flex-1 leading-none">
              {{ currentPriority.label }}
            </span>
            <span
              class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
            />
          </button>
          <div
            v-if="showPriorityMenu"
            class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[160px] py-1 overflow-hidden"
            @click.stop
          >
            <button
              v-for="(opt, idx) in filteredPriorityOptions"
              :key="opt.key"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
              :class="
                idx === priorityHighlight
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : opt.key === local.priority
                    ? 'text-n-brand font-medium'
                    : 'text-n-slate-12'
              "
              @click="setPriority(opt.key)"
              @mouseenter="priorityHighlight = idx"
            >
              <span
                class="i-lucide-flag size-3.5 flex-shrink-0"
                :class="opt.flag"
              />
              {{ opt.label }}
              <span
                v-if="opt.key === local.priority"
                class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Linha 2: Prazo | Recorrência -->
      <div
        class="grid grid-cols-2 divide-x divide-n-weak border-t border-n-weak"
      >
        <!-- Prazo -->
        <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.FIELDS.DUE_DATE') }}
          </p>
          <div class="flex items-center gap-2">
            <input
              :value="local.due_value ?? 0"
              type="number"
              min="0"
              class="reset-base !w-20 !h-8 shrink-0 rounded-lg border border-n-weak bg-n-surface-2 pl-2 pr-1 text-sm text-n-slate-12 focus:outline-none appearance-auto"
              @input="patch('due_value', Number($event.target.value))"
            />
            <AutomationSelect
              compact
              :model-value="local.due_unit"
              :options="DUE_UNITS"
              @update:model-value="patch('due_unit', $event)"
            />
          </div>
        </div>

        <!-- Recorrência -->
        <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.RECURRENCE.LABEL') }}
          </p>
          <div class="relative">
            <button
              ref="recurrenceBtnRef"
              type="button"
              class="flex items-center gap-1.5 px-2.5 !h-8 rounded-lg border transition-colors focus:outline-none"
              :class="
                local.recurrence_type
                  ? 'border-n-weak bg-n-surface-2 text-n-brand'
                  : 'border-n-weak bg-n-surface-2 text-n-slate-9 hover:text-n-slate-11'
              "
              @click.stop="showRecurrencePopover = !showRecurrencePopover"
            >
              <span class="i-lucide-repeat size-3.5 flex-shrink-0" />
              <span class="text-sm truncate">{{ recurrenceLabel }}</span>
            </button>
            <TaskGroupItemRecurrencePopover
              v-if="showRecurrencePopover"
              :item="local"
              :anchor-el="recurrenceBtnRef"
              @update="onRecurrenceUpdate"
              @close="showRecurrencePopover = false"
            />
          </div>
        </div>
      </div>

      <!-- Linha 3: Tipo | Etiquetas -->
      <div
        class="grid grid-cols-2 divide-x divide-n-weak border-t border-n-weak"
      >
        <!-- Tipo -->
        <div class="flex flex-col gap-1 px-3 py-2.5 relative" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.FIELDS.TASK_TYPE') }}
          </p>
          <button
            class="flex items-center gap-1.5 text-sm w-full text-left outline-none"
            @click="showTypeMenu = !showTypeMenu"
          >
            <span
              v-if="showTypeMenu && typeSearch"
              class="text-n-slate-10 truncate flex-1 leading-none"
            >
              {{ typeSearch }}
            </span>
            <template v-else>
              <span class="size-4 flex-shrink-0" :class="currentType.icon" />
              <span class="text-n-slate-12 truncate flex-1 leading-none">{{
                currentType.label
              }}</span>
            </template>
            <span
              class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
            />
          </button>
          <div
            v-if="showTypeMenu"
            class="absolute left-0 top-full mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-full min-w-[160px] py-1 overflow-hidden"
            @click.stop
          >
            <button
              v-for="(tp, idx) in filteredTaskTypes"
              :key="tp.key"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
              :class="
                idx === typeHighlight
                  ? 'text-n-brand font-medium bg-n-alpha-1'
                  : tp.key === local.task_type
                    ? 'text-n-brand font-medium'
                    : 'text-n-slate-12'
              "
              @click="setTaskType(tp.key)"
              @mouseenter="typeHighlight = idx"
            >
              <span class="size-4 flex-shrink-0" :class="tp.icon" />
              {{ tp.label }}
              <span
                v-if="tp.key === local.task_type"
                class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
              />
            </button>
          </div>
        </div>

        <!-- Etiquetas -->
        <div class="flex flex-col gap-1 px-3 py-2.5" @click.stop>
          <p
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ t('TASKS.FIELDS.LABELS') }}
          </p>
          <div class="flex flex-wrap items-center gap-1 relative">
            <span
              v-for="tag in local.label_list ?? []"
              :key="tag"
              class="group/tag inline-flex items-center h-4.5 text-[10px] font-medium px-1 rounded-full"
              :style="{
                backgroundColor: tagColor(tag).bg,
                color: tagColor(tag).text,
              }"
            >
              {{ tag }}
              <button
                class="ml-0.5 opacity-0 group-hover/tag:opacity-100 transition-opacity flex items-center justify-center"
                @click.stop="removeLabel(tag)"
              >
                <span class="i-lucide-x size-2.5" />
              </button>
            </span>
            <div class="relative">
              <button
                class="flex items-center gap-1 text-xs text-n-slate-9 hover:text-n-slate-12 transition-colors"
                @click.stop="showLabelMenu = !showLabelMenu"
              >
                <span
                  v-if="showLabelMenu && labelSearch"
                  class="text-[11px] text-n-slate-10"
                >
                  {{ labelSearch }}
                </span>
                <span v-else class="i-lucide-tag size-3.5" />
              </button>
              <div
                v-if="showLabelMenu"
                class="absolute left-0 top-6 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 py-1 overflow-hidden"
                @click.stop
              >
                <button
                  v-for="(lbl, idx) in filteredLabels"
                  :key="lbl.id"
                  class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors focus:outline-none"
                  :class="
                    idx === labelHighlight
                      ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                      : isLabelActive(lbl.title)
                        ? 'text-n-slate-12 font-medium'
                        : 'text-n-slate-11'
                  "
                  @click="toggleLabel(lbl.title)"
                  @mouseenter="labelHighlight = idx"
                >
                  <span
                    class="size-2.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: lbl.color }"
                  />
                  <span class="truncate">{{ lbl.title }}</span>
                  <span
                    v-if="isLabelActive(lbl.title)"
                    class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
                  />
                </button>
                <p
                  v-if="!filteredLabels.length"
                  class="px-3 py-2 text-xs text-n-slate-10 italic text-center"
                >
                  {{ t('TASKS.TAGS.EMPTY') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Descrição -->
    <div class="flex flex-col gap-2">
      <p
        class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
      >
        {{ t('TASKS.FIELDS.DESCRIPTION') }}
      </p>
      <textarea
        :value="local.description ?? ''"
        rows="3"
        :placeholder="t('TASKS.FORM.DESCRIPTION_PLACEHOLDER')"
        class="w-full text-sm text-n-slate-12 bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-n-brand placeholder:text-n-slate-9"
        @change="patch('description', $event.target.value)"
      />
    </div>

    <!-- Checklist -->
    <div class="flex flex-col gap-2">
      <p
        class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
      >
        {{ t('TASKS.CHECKLIST.TITLE') }}
        <span
          v-if="checklistProgress"
          class="ml-1 text-n-slate-9 normal-case tracking-normal font-normal"
        >
          {{ checklistProgress.label }}
        </span>
      </p>

      <!-- Itens existentes -->
      <div class="flex flex-col gap-1">
        <div
          v-for="(ci, idx) in checklistItems"
          :key="ci.id"
          draggable="true"
          class="group/ci flex items-center gap-2 py-1 rounded transition-colors"
          :class="dragOverIndex === idx ? 'bg-n-alpha-1' : ''"
          @dragstart="onDragStart(idx)"
          @dragover="onDragOver($event, idx)"
          @dragleave="onDragLeave"
          @drop="onDrop(idx)"
          @dragend="onDragEnd"
        >
          <span
            class="i-lucide-grip-vertical size-3.5 text-n-slate-7 flex-shrink-0 cursor-grab opacity-0 group-hover/ci:opacity-100 transition-opacity"
          />

          <!-- Checkbox (não clicável — este é um template) -->
          <div
            class="flex-shrink-0 flex items-center justify-center w-5 h-5 min-w-[20px] cursor-default opacity-60"
          >
            <svg
              v-if="ci.completed"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgb(var(--teal-9))"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="flex-shrink-0"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgb(var(--slate-7))"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="flex-shrink-0"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" />
            </svg>
          </div>

          <!-- Texto -->
          <span
            class="flex-1 text-sm min-w-0 truncate"
            :class="
              ci.completed ? 'line-through text-n-slate-10' : 'text-n-slate-12'
            "
          >
            {{ ci.text }}
          </span>

          <!-- Avatar responsável -->
          <div class="relative flex-shrink-0" @click.stop>
            <button
              class="size-5 rounded-full flex items-center justify-center hover:opacity-80 transition-all"
              :style="checklistItemAvatarStyle(ci.assignee_id)"
              :class="
                !ci.assignee_id
                  ? 'bg-n-surface-2 border border-n-weak opacity-0 group-hover/ci:opacity-60'
                  : ''
              "
              @click="
                showChecklistAssignee === ci.id
                  ? (showChecklistAssignee = null)
                  : (closeDropdowns(), (showChecklistAssignee = ci.id))
              "
            >
              <span
                v-if="
                  ci.assignee_id &&
                  !agents?.find(a => a.id === ci.assignee_id)?.thumbnail
                "
                class="text-[8px] font-bold text-white"
              >
                {{ initials(agents?.find(a => a.id === ci.assignee_id)?.name) }}
              </span>
              <span
                v-else-if="!ci.assignee_id"
                class="i-lucide-user-plus size-2.5 text-n-slate-9"
              />
            </button>
            <div
              v-if="showChecklistAssignee === ci.id"
              class="absolute right-0 top-6 z-30 bg-n-background border border-n-weak rounded-lg shadow-xl w-48 overflow-hidden"
              @click.stop
            >
              <button
                class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                @click="setChecklistItemAssignee(ci.id, null)"
              >
                <span
                  class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
                />
                {{ t('TASKS.CHECKLIST.NO_ASSIGNEE') }}
              </button>
              <div class="border-t border-n-weak" />
              <button
                v-for="agent in agents"
                :key="agent.id"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
                :class="
                  agent.id === ci.assignee_id
                    ? 'text-n-brand font-medium'
                    : 'text-n-slate-12'
                "
                @click="setChecklistItemAssignee(ci.id, agent.id)"
              >
                <div
                  class="size-4 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                  :style="
                    !agent.thumbnail
                      ? { backgroundColor: avatarColor(agent) }
                      : {}
                  "
                >
                  <img
                    v-if="agent.thumbnail"
                    :src="agent.thumbnail"
                    class="size-full object-cover"
                  />
                  <span v-else class="text-[8px] font-bold text-white">{{
                    initials(agent.name)
                  }}</span>
                </div>
                <span class="truncate">{{ agent.name }}</span>
                <span
                  v-if="agent.id === ci.assignee_id"
                  class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
                />
              </button>
            </div>
          </div>

          <!-- Remover -->
          <button
            class="flex-shrink-0 opacity-0 group-hover/ci:opacity-60 hover:opacity-100 transition-opacity"
            @click="removeChecklistItem(ci.id)"
          >
            <span class="i-lucide-x size-3.5 text-n-slate-9" />
          </button>
        </div>
      </div>

      <!-- Input novo item -->
      <div class="flex items-center gap-2">
        <span class="i-lucide-plus size-3.5 text-n-slate-9 flex-shrink-0" />
        <input
          ref="newChecklistInputRef"
          v-model="newChecklistText"
          class="flex-1 text-sm text-n-slate-12 bg-transparent outline-none border-none placeholder:text-n-slate-9"
          :placeholder="t('TASKS.CHECKLIST.ADD_PLACEHOLDER')"
          @keydown.enter.prevent="addChecklistItem"
          @keydown.esc="newChecklistText = ''"
          @click.stop
        />
        <button
          v-if="newChecklistText.trim()"
          class="text-xs text-n-brand font-medium hover:opacity-80 transition-opacity flex-shrink-0 whitespace-nowrap"
          @click="addChecklistItem"
        >
          {{ t('TASKS.CHECKLIST.ADD_BUTTON') }}
        </button>
      </div>
    </div>
  </div>
</template>
